/**
 * The route contract: one place that says what every endpoint answers with.
 *
 * Both sides import this. The worker's handlers are typed against it so a
 * response that does not match stops at `tsc`, and the web app's client is
 * typed against the same names so a renamed field breaks the build rather than
 * the page. It is a hand-written contract rather than a generated one on
 * purpose -- there are two dozen routes, and the generator would be more code
 * than the thing it generates.
 */

import type {
  AdminAccount,
  AdminSummary,
  AllowlistEntry,
  CategoryCount,
  FieldInput,
  FieldTemplate,
  HealthReport,
  InventoryMember,
  InventoryMembership,
  InventoryMeta,
  InventoryReport,
  Item,
  ItemCondition,
  ItemPage,
  ItemStatus,
  LocationCount,
  PasskeyCreationOptions,
  PasskeyCreationResponse,
  PasskeyRequestOptions,
  PasskeyRequestResponse,
  PasskeySummary,
  PhotoMeta,
  SavedSearch,
  SessionUser,
  TagCount,
} from "./types.js";
import type {
  AttachmentMeta,
  BillingLine,
  Client,
  ClientColor,
  DeployInput,
  Deployment,
  ExportTemplate,
  ExportTemplateSpec,
  InventoryKind,
  Receipt,
  ReceiptInput,
  RetireInput,
  Retirement,
  ReturnInput,
  TransferInput,
  StockDetail,
  StockOverview,
  StockRecords,
  StockSettings,
} from "./stock.js";
import type {
  AlertPrefs,
  HoldInput,
  QuietInput,
  ReleaseInput,
  Reserve,
  ReserveDetail,
  ReserveInput,
  ReserveTag,
} from "./reserves.js";
import type { ReportFacts, ReportSpec, SavedReport } from "./reportBuilder.js";

/**
 * Every failure looks the same, and `reason` is a sentence somebody can read.
 * illgati's convention, kept: the client never has to map a status code onto
 * words, and the words live next to the check that produced them.
 */
export interface ApiError {
  ok: false;
  reason: string;
  /** Only on 503s from the configuration guard -- names the bindings or secrets that are absent. */
  missing?: string[];
}

export type ApiResult<T> = ({ ok: true } & T) | ApiError;

/** `GET /api/me` answers for a signed-out visitor too, which is how the app knows to show the door. */
export interface MeResponse {
  user: SessionUser | null;
}

export interface AuthStartRequest {
  email: string;
}

export interface AuthStartResponse {
  /**
   * Whether mail actually went out. False with a reason means the address is
   * not on the allowlist -- see docs/notes/auth.md for why this app says so
   * plainly instead of giving the neutral "if that address is allowed" answer.
   */
  sent: true;
}

export interface InventoryMetaResponse {
  inventory: InventoryMeta;
}

/* --------------------------------------------------------------- passkeys */

export interface PasskeyListResponse {
  passkeys: PasskeySummary[];
}

/** `POST /api/auth/passkey/register/options`. Signed in: excludes credentials the account already has. */
export interface PasskeyRegisterOptionsResponse {
  options: PasskeyCreationOptions;
}

export interface PasskeyRegisterVerifyRequest {
  response: PasskeyCreationResponse;
  /** An optional nickname, offered after the ceremony completes rather than before it. */
  name?: string;
}

export type PasskeyRegisterVerifyResponse = PasskeyListResponse;

/** `POST /api/auth/passkey/login/options`. Signed out: discoverable, no `allowCredentials`. */
export interface PasskeyLoginOptionsResponse {
  options: PasskeyRequestOptions;
}

export interface PasskeyLoginVerifyRequest {
  response: PasskeyRequestResponse;
}

export type PasskeyLoginVerifyResponse = MeResponse;

/**
 * Which inventory an inventory route acts on. Absent, it is the caller's own
 * -- every account has exactly one of those, from signup -- so this is the one
 * header only a browser that has actually switched ever sends, and everything
 * before Stage 5 keeps working without knowing it exists.
 */
export const INVENTORY_HEADER = "X-Inventory-Id";

export interface InventoryMembersResponse {
  members: InventoryMember[];
}

export interface InventoryMemberAddRequest {
  email: string;
  /** Editor when left out. */
  role?: "editor" | "viewer";
}

export interface AdminSummaryResponse {
  summary: AdminSummary;
}

export interface AdminAllowlistResponse {
  allowlist: AllowlistEntry[];
}

export interface AdminAccountsResponse {
  accounts: AdminAccount[];
}

export interface AdminAllowlistAddRequest {
  email: string;
  note?: string;
}

export interface AdminAccountStatusRequest {
  status: "active" | "disabled";
}

export interface AdminAccountStockRequest {
  enabled: boolean;
}

export interface AdminAccountQuotaRequest {
  quotaBytes: number;
}

/* ------------------------------------------------------------ stock (7) -- */

export interface InventoryCreateRequest {
  name: string;
  kind: InventoryKind;
  /** An ISO code from `CURRENCIES`; dollars when left out. */
  currency?: string;
}

/** `PATCH /api/inventories/:id`: a new name, a new currency, or both. */
export interface InventoryRenameRequest {
  name?: string;
  currency?: string;
}

/** What renaming, archiving, restoring, leaving or deleting answers with: the switcher's list, fresh. */
export interface InventoriesResponse {
  memberships: InventoryMembership[];
}

/** Answers with the new inventory's id and every membership, so the switcher can move straight to it. */
export interface InventoryCreateResponse {
  inventoryId: string;
  memberships: InventoryMembership[];
}

export type StockResponse = StockOverview;

export type StockRecordsResponse = StockRecords;

export interface StockSettingsResponse {
  settings: StockSettings;
}

export interface ClientsResponse {
  clients: Client[];
}

/**
 * POST names a new client. PATCH changes whichever of the two it is given: a
 * rename, a colour (null for the plain grey), or both.
 */
export interface ClientWriteRequest {
  name?: string;
  color?: ClientColor | null;
}

/** PATCH on one deployment: where at the client it is. An empty string clears it. */
export interface DeploymentSpotRequest {
  spot: string;
}

/** PATCH on a reserve tag: its new name, on every item that wears it and every rule that matches on it. */
export interface ReserveTagRenameRequest {
  name: string;
}

export interface ReceiptsResponse {
  receipts: Receipt[];
}

export type ReceiptCreateRequest = ReceiptInput;

export interface ReceiptCreateResponse {
  receipt: Receipt;
  itemIds: string[];
}

export interface AttachmentUploadResponse {
  attachment: AttachmentMeta;
  usedBytes: number;
}

export type DeployRequest = DeployInput;

export interface DeployResponse {
  deployments: Deployment[];
}

export type ReturnRequest = ReturnInput;

/** Answered like a deploy: the new client's deployments. */
export type TransferRequest = TransferInput;

export interface ReturnResponse {
  deployment: Deployment;
}

export type RetireRequest = RetireInput;

/**
 * Price stock at its unit cost plus the standard markup. Without `overwrite`
 * only items with no price are touched; with it, every priced item is reset too.
 * `itemIds` narrows it to those items (what is ticked on the Stock screen);
 * absent, it is every stock item that is not retired.
 */
export interface PriceFromCostRequest {
  overwrite: boolean;
  itemIds?: string[];
}

export interface PriceFromCostResponse {
  /** How many items were given a new price. */
  priced: number;
}

export interface RetireResponse {
  retirement: Retirement;
}

export type ItemStockResponse = StockDetail;

export interface BillingResponse {
  lines: BillingLine[];
}

export interface BillingMarkRequest {
  deploymentIds: string[];
}

export interface BillingMarkResponse {
  marked: number;
}

export interface ExportTemplatesResponse {
  /** The shipped presets first, then this inventory's own. */
  templates: ExportTemplate[];
}

export type ExportTemplateCreateRequest = ExportTemplateSpec;

/* --------------------------------------------------- reserves (back stock) */

/** `GET /api/reserves`: every reserve, and the signed-in person's own alert settings for this stockroom. */
export interface ReservesResponse {
  reserves: Reserve[];
  alerts: AlertPrefs;
  reserveTags: ReserveTag[];
}

/** `GET` and `POST /api/reserves/tags` and `DELETE /api/reserves/tags/:name` all answer with the list, fresh. */
export interface ReserveTagsResponse {
  reserveTags: ReserveTag[];
}

export type ReserveWriteRequest = ReserveInput;

export interface ReserveResponse {
  reserve: Reserve;
}

export type ReserveDetailResponse = ReserveDetail;

export type HoldRequest = HoldInput;

export interface FillRequest {
  heldOn: string;
}

export type ReleaseRequest = ReleaseInput;

export type QuietRequest = QuietInput;

export type AlertPrefsRequest = AlertPrefs;

export interface AlertPrefsResponse {
  alerts: AlertPrefs;
}

/* ------------------------------------------------------------------ items */

/** A grid's worth of items. `GET /api/items`, with the filters in the query string. */
export type ItemsResponse = ItemPage;

/** One item, whole. `GET`, `POST` and `PATCH` all answer with this same shape. */
export interface ItemResponse {
  item: Item;
}

export interface ItemWriteRequest {
  title?: string;
  description?: string;
  notes?: string;
  locationId?: string | null;
  categoryId?: string | null;
  priceCents?: number | null;
  condition?: ItemCondition;
  status?: ItemStatus;
  quantity?: number;
  /** The item's whole field list, replacing what is there. Leave it out to leave the fields alone. */
  fields?: FieldInput[];
  /** The item's whole tag list, replacing what is there. Leave it out to leave the tags alone. */
  tags?: string[];
}

/**
 * A delete answers with the account's new storage figure rather than nothing,
 * because the screen that just removed an item is the screen showing how full
 * the account is. One round trip instead of two.
 */
export interface ItemDeleteResponse {
  usedBytes: number;
}

export interface LocationsResponse {
  locations: LocationCount[];
}

export interface LocationCreateRequest {
  name: string;
  parentId?: string | null;
}

/** `PATCH /api/locations/:id`: either or both. A `parentId` of null is the top level. */
export interface LocationUpdateRequest {
  name?: string;
  parentId?: string | null;
}

export interface CategoriesResponse {
  categories: CategoryCount[];
}

export interface CategoryCreateRequest {
  name: string;
}

export interface TagsResponse {
  tags: TagCount[];
}

/** `GET /api/report`, Stage 6's estimated value and status breakdown. */
export interface ReportResponse {
  report: InventoryReport;
}

/** `GET /api/reports/facts`. */
export interface ReportFactsResponse {
  facts: ReportFacts;
}

/** `GET`, `POST` and `DELETE /api/reports` all answer with the list, fresh. */
export interface SavedReportsResponse {
  reports: SavedReport[];
}

export interface SavedReportCreateRequest {
  name: string;
  spec: ReportSpec;
}

export interface FieldTemplatesResponse {
  templates: FieldTemplate[];
}

export interface FieldTemplateCreateRequest {
  label: string;
  type?: string;
  group?: string;
  visibility?: string;
}

export interface SavedSearchesResponse {
  searches: SavedSearch[];
}

export interface SavedSearchCreateRequest {
  name: string;
  query: string;
}

/**
 * What an upload answers with: the photo, and what the account now holds. The
 * capture screen shows the second the moment the first lands, so a sitting that
 * is filling an account says so while it is happening rather than at the end.
 */
export interface PhotoUploadResponse {
  photo: PhotoMeta;
  usedBytes: number;
}

export interface PhotoDeleteResponse {
  usedBytes: number;
}

/** `PATCH /api/items/:id/photos`: the new photo order, as the whole list of ids, cover first. */
export interface PhotoReorderRequest {
  order: string[];
}

export interface PhotoReorderResponse {
  photos: PhotoMeta[];
}

/** The multipart field names an upload uses. Spelled once, so both sides agree. */
export const PHOTO_PART = "photo";
export const THUMB_PART = "thumb";
export const WIDTH_PART = "width";
export const HEIGHT_PART = "height";

/**
 * Path constants, so a route is spelled once. The worker matches on these and
 * the client calls them; a typo is then a compile error on one side or the
 * other rather than a 404 nobody notices until the button is pressed.
 *
 * The item, location, category and photo routes take ids, so they are prefixes
 * with a builder below rather than whole paths.
 */
export const ROUTES = {
  health: "/api/health",
  me: "/api/me",
  authStart: "/api/auth/email/start",
  authVerify: "/api/auth/email/verify",
  logout: "/api/auth/logout",
  /** Ends every session the account holds, on every device, this one included. */
  logoutEverywhere: "/api/auth/logout-everywhere",
  passkeys: "/api/auth/passkey",
  passkeyRegisterOptions: "/api/auth/passkey/register/options",
  passkeyRegisterVerify: "/api/auth/passkey/register/verify",
  passkeyLoginOptions: "/api/auth/passkey/login/options",
  passkeyLoginVerify: "/api/auth/passkey/login/verify",
  /** GET, a navigation like authVerify -- not something a fetch() caller reads. */
  googleStart: "/api/auth/google/start",
  googleCallback: "/api/auth/google/callback",
  microsoftStart: "/api/auth/microsoft/start",
  microsoftCallback: "/api/auth/microsoft/callback",
  inventoryMeta: "/api/inventory/meta",
  inventoryMembers: "/api/inventory/members",
  items: "/api/items",
  locations: "/api/locations",
  categories: "/api/categories",
  tags: "/api/tags",
  report: "/api/report",
  /** Stage 6, reports you build: GET the saved ones, POST one; `/:id` DELETE. */
  reports: "/api/reports",
  /** GET `?from=YYYY-MM-DD&to=YYYY-MM-DD`: the rows a built report groups. */
  reportFacts: "/api/reports/facts",
  fieldTemplates: "/api/field-templates",
  searches: "/api/searches",
  photos: "/api/photos",
  /** Stage 7. POST makes a new inventory the caller owns. */
  inventories: "/api/inventories",
  stock: "/api/stock",
  stockSettings: "/api/stock/settings",
  /** GET: every stock record, for the export. */
  stockRecords: "/api/stock/records",
  stockDeploy: "/api/stock/deploy",
  stockReturn: "/api/stock/return",
  /** Straight from one client to another: a return and a deploy, as one write. */
  stockTransfer: "/api/stock/transfer",
  stockRetire: "/api/stock/retire",
  /** Serialized units into one counted line. */
  stockMerge: "/api/stock/merge",
  /** A counted line into one serialized item per unit. */
  stockSplit: "/api/stock/split",
  /** PATCH `/:id`: where at the client one deployment is. */
  stockDeployments: "/api/stock/deployments",
  /** POST: set item prices from unit cost plus the standard markup. */
  stockPriceFromCost: "/api/stock/price-from-cost",
  clients: "/api/clients",
  receipts: "/api/receipts",
  retirements: "/api/retirements",
  attachments: "/api/attachments",
  billing: "/api/billing",
  billingMark: "/api/billing/mark",
  exportTemplates: "/api/export-templates",
  /** Back stock: reserves, the holds in them, and each person's alert settings. */
  reserves: "/api/reserves",
  reserveAlerts: "/api/reserves/alerts",
  reserveTags: "/api/reserves/tags",
  adminSummary: "/api/admin/summary",
  adminAllowlist: "/api/admin/allowlist",
  adminAccounts: "/api/admin/accounts",
  adminInventories: "/api/admin/inventories",
} as const;

/** `/api/items/:id`, and the same for anything else addressed by one. */
export const itemPath = (id: string): string => `${ROUTES.items}/${encodeURIComponent(id)}`;

/** `/api/field-templates/:id` and `/api/searches/:id`, for the two lists somebody prunes. */
export const fieldTemplatePath = (id: string): string => `${ROUTES.fieldTemplates}/${encodeURIComponent(id)}`;
export const savedSearchPath = (id: string): string => `${ROUTES.searches}/${encodeURIComponent(id)}`;

/** Where an item's photos are posted. */
export const itemPhotosPath = (id: string): string => `${itemPath(id)}/photos`;

/** `/api/inventory/members/:userId`, for removing one. */
export const inventoryMemberPath = (userId: string): string =>
  `${ROUTES.inventoryMembers}/${encodeURIComponent(userId)}`;

/** Stage 7's addressed routes. `/api/inventories/:id`, and its actions under it. */
export const inventoryPath = (id: string, action?: "archive" | "restore" | "leave"): string =>
  `${ROUTES.inventories}/${encodeURIComponent(id)}${action ? `/${action}` : ""}`;
export const itemStockPath = (id: string): string => `${itemPath(id)}/stock`;
export const clientPath = (id: string): string => `${ROUTES.clients}/${encodeURIComponent(id)}`;
export const deploymentPath = (id: string): string => `${ROUTES.stockDeployments}/${encodeURIComponent(id)}`;
export const receiptAttachmentPath = (id: string): string =>
  `${ROUTES.receipts}/${encodeURIComponent(id)}/attachment`;
export const retirementCertificatePath = (id: string): string =>
  `${ROUTES.retirements}/${encodeURIComponent(id)}/certificate`;
export const attachmentPath = (id: string): string => `${ROUTES.attachments}/${encodeURIComponent(id)}`;
export const exportTemplatePath = (id: string): string =>
  `${ROUTES.exportTemplates}/${encodeURIComponent(id)}`;
/** `/api/reserves/:id`, and its actions under it. */
export const reservePath = (id: string, action?: "hold" | "fill" | "quiet"): string =>
  `${ROUTES.reserves}/${encodeURIComponent(id)}${action ? `/${action}` : ""}`;
/** `/api/reserves/tags/:name`, for renaming (PATCH) or unflagging (DELETE) one. */
export const reserveTagPath = (name: string): string => `${ROUTES.reserveTags}/${encodeURIComponent(name)}`;
/** `/api/reserves/holds/:id/release`. */
export const releasePath = (holdId: string): string =>
  `${ROUTES.reserves}/holds/${encodeURIComponent(holdId)}/release`;

/**
 * The same inventory id `INVENTORY_HEADER` carries, as a cookie -- for the
 * requests a browser makes by itself (`<img src>`), which cannot carry a
 * header. Checked against `memberships` exactly the way the header is.
 */
export const INVENTORY_COOKIE = "utucat_inventory";

/** `/api/auth/passkey/:id`, for removing one. */
export const passkeyPath = (id: string): string => `${ROUTES.passkeys}/${encodeURIComponent(id)}`;

/**
 * The full-size photo, and the thumbnail a card shows. Both go through the
 * worker, which checks the session and the ownership before it reads R2 --
 * there is no public bucket and no signed URL to leak.
 */
export const photoPath = (id: string): string => `${ROUTES.photos}/${encodeURIComponent(id)}`;
export const thumbPath = (id: string): string => `${photoPath(id)}/thumb`;

export type {
  HealthReport,
  SessionUser,
  InventoryMeta,
  AllowlistEntry,
  AdminAccount,
  AdminSummary,
  Item,
  ItemPage,
  PhotoMeta,
  LocationCount,
  CategoryCount,
};
